import React, { useCallback, useEffect, useMemo, useState } from "react";
import SubHeader from "../../components/SubHeader/SubHeader";
import SelectInput from "../../components/SelectInput/SelectInput";
import StatCard from "../../components/StatCard/StatCard";
import Loader from "../../components/Loader/Loader";
import PatientsIcon from "../../assets/icons/navbarIcons/PatientsIcon";
import DashboardIcon from "../../assets/icons/navbarIcons/DashboardIcon";
import CardIcon from "../../assets/icons/navbarIcons/CardIcon";
import PendingIcon from "../../assets/icons/navbarIcons/PendingIcon";
import { errorToast } from "../../services/ToastHelper";
import { postApi, getApi } from "../../utils/apiService";
import { DASHBOARD_API, GET_ALL_BRANCH, GET_ASSESSMENTTYPES, GET_ASSESSMENTTYPE_SCHEDULE } from "../../utils/apiPath";
import { getStoredBranchOptions } from "../../services/auth";
import "./Dashboard.scss";

const DEFAULT_GRADE_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  label: `Grade ${i + 1}`,
  value: i + 1,
}));

const SECTION_OPTIONS = ["A", "B", "C", "D", "E", "F", "G", "H"].map((s) => ({
  label: `Section ${s}`,
  value: s,
}));

const STAT_CONFIG = [
//   { key: "totalStudents", title: "Total Students", Icon: PatientsIcon },
  { key: "totalParticipants", title: "Participated", Icon: DashboardIcon },
//   { key: "totalNotParticipated", title: "Not Participated", Icon: PendingIcon },
//   { key: "totalAssessments", title: "Total Assessments", Icon: CardIcon },
//   { key: "participationPercentage", title: "Participation %", Icon: DashboardIcon, suffix: "%" },
//   { key: "averageScore", title: "Average Score", Icon: CardIcon },
];

const Dashboard = () => {
  const role = sessionStorage.getItem("role");
  const isAdmin = role === "Admin";
  const isStaff = role === "Staff";

  const sessionBranchId = useMemo(() => {
    const raw = sessionStorage.getItem("branchId");
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  // Branches assigned to the logged-in staff (admins pick from all branches instead)
  const staffBranchOptions = useMemo(() => getStoredBranchOptions(), []);
  // Show the branch dropdown for admins, or for staff assigned to more than one branch
  const showBranchSelect = isAdmin || staffBranchOptions.length > 1;

  const gradeOptions = useMemo(() => {
    if (!isStaff) return DEFAULT_GRADE_OPTIONS;
    try {
      const list = JSON.parse(sessionStorage.getItem("gradeAccess") || "[]");
      return (list || []).map((g) => ({
        label: g.gradeName || `Grade ${g.gradeId}`,
        value: g.gradeId,
      }));
    } catch {
      return [];
    }
  }, [isStaff]);

  const [isLoading, setIsLoading] = useState(false);
  const [branchesList, setBranchesList] = useState([]);
  const [assessmentOptions, setAssessmentOptions] = useState([]);
  const [scheduleOptions, setScheduleOptions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    branchId: isAdmin ? 0 : sessionBranchId,
    gradeId: 0,
    sectionName: "",
    assessmentTypeId: 0,
    scheduleId: 0,
  });

  const fetchBranches = useCallback(async () => {
    const { statusCode, data } = await getApi(GET_ALL_BRANCH);
    if (statusCode === 200) {
      const list = Array.isArray(data) ? data : data?.data || [];
      setBranchesList(
        list.map((b) => ({
          label: b.name,
          value: b.branchId,
        }))
      );
    }
  }, []);

  const fetchAssessmentTypes = useCallback(async () => {
    const { statusCode, data } = await getApi(GET_ASSESSMENTTYPES);
    if (statusCode === 200) {
      const list = Array.isArray(data) ? data : data?.data || [];
      setAssessmentOptions(list.map((a) => ({ label: a.name, value: a.id })));
    }
  }, []);

  const fetchSchedules = useCallback(async (assessmentTypeId) => {
    if (!assessmentTypeId) {
      setScheduleOptions([]);
      return;
    }
    const { statusCode, data } = await getApi(GET_ASSESSMENTTYPE_SCHEDULE, {
      params: { assessmentTypeId, attendedOnly: true },
    });
    if (statusCode === 200) {
      const list = Array.isArray(data) ? data : data?.data || [];
      setScheduleOptions(
        list.map((s) => ({
          label: `${s.scheduledDate} ${s.scheduledTime}`,
          value: s.id,
        }))
      );
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    setIsLoading(true);
    const payload = {
      branchId: filters.branchId || null,
      gradeId: filters.gradeId || null,
      sectionName: filters.sectionName || "",
      assessmentTypeId: filters.assessmentTypeId || null,
      assessmentTypeScheduleId: filters.scheduleId || null,
    };
    const { statusCode, data, message } = await postApi(DASHBOARD_API, payload);
    if (statusCode === 200) {
      setSummary(data || {});
    } else {
      errorToast(message);
      setSummary({});
    }
    setIsLoading(false);
  }, [filters]);

  useEffect(() => {
    if (isAdmin) fetchBranches();
  }, [isAdmin, fetchBranches]);

  useEffect(() => {
    fetchAssessmentTypes();
  }, [fetchAssessmentTypes]);

  // Load schedules whenever the selected assessment type changes
  useEffect(() => {
    fetchSchedules(filters.assessmentTypeId);
  }, [filters.assessmentTypeId, fetchSchedules]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const statCards = useMemo(() => {
    if (!summary) return [];
    return STAT_CONFIG.map((cfg) => {
      const raw = summary[cfg.key];
      const value =
        raw === undefined || raw === null || raw === ""
          ? 0
          : raw;
      return {
        ...cfg,
        value: cfg.suffix ? `${value}${cfg.suffix}` : value,
      };
    });
  }, [summary]);

  return (
    <div className="dashboard-page">
      {isLoading && <Loader />}

      <SubHeader title="Dashboard" showBack={false} />

      <div className={`dashboard-filters ${showBranchSelect ? "cols-5" : "cols-4"}`}>
        {showBranchSelect && (
          <SelectInput
            title="Branch"
            placeholder="Select branch"
            value={filters.branchId || undefined}
            options={isAdmin ? branchesList : staffBranchOptions}
            onChange={(val) =>
              setFilters((prev) => ({ ...prev, branchId: val || 0 }))
            }
          />
        )}
        <SelectInput
          title="Assessment Type"
          placeholder="Select assessment type"
          value={filters.assessmentTypeId || undefined}
          options={assessmentOptions}
          allowClear
          onChange={(val) =>
            setFilters((prev) => ({
              ...prev,
              assessmentTypeId: val || 0,
              // reset dependent schedule selection when the assessment type changes
              scheduleId: 0,
            }))
          }
        />
        <SelectInput
          title="Scheduled On"
          placeholder="Select schedule"
          value={filters.scheduleId || undefined}
          options={scheduleOptions}
          disabled={!filters.assessmentTypeId}
          allowClear
          onChange={(val) =>
            setFilters((prev) => ({ ...prev, scheduleId: val || 0 }))
          }
        />
        <SelectInput
          title="Grade"
          placeholder="Select grade"
          value={filters.gradeId || undefined}
          options={gradeOptions}
          onChange={(val) =>
            setFilters((prev) => ({ ...prev, gradeId: val || 0 }))
          }
        />
        <SelectInput
          title="Section"
          placeholder="Select section"
          value={filters.sectionName || undefined}
          options={SECTION_OPTIONS}
          onChange={(val) =>
            setFilters((prev) => ({ ...prev, sectionName: val || "" }))
          }
        />
      </div>

      <div className="dashboard-stats">
        {statCards.map((c) => (
          <StatCard
            key={c.key}
            title={c.title}
            value={c.value}
            Icon={c.Icon}
          />
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
